import React from 'react'
import { cn } from '../../lib/utils'

export interface PageHeaderProps {
  /** The page's name. Rendered as the page's single `<h1>`. */
  title: React.ReactNode
  /**
   * One line under the title saying what the page is for. Takes nodes so a
   * subtitle can carry an inline link.
   */
  description?: React.ReactNode
  /**
   * Primary page actions, right-aligned on the same row as the title
   * (an "Add organization" button, an export control). Render them from
   * first paint and disable until data is ready, rather than hiding them —
   * an action that appears late makes a loaded page look unfinished.
   */
  actions?: React.ReactNode
  className?: string
}

/**
 * The standard heading block for a top-level page — one that a sidebar entry
 * points at directly.
 *
 * This exists because the same four lines of JSX were hand-written in 37 files
 * across dashboard, helpdesk and admin, and they had drifted: two pages carried
 * `text-3xl tracking-tight` (playbook's house style, copied across app
 * boundaries), several used `text-gray-900 dark:text-white` instead of the
 * `text-foreground` token, three used `text-gray-900` with no dark variant at
 * all — near-black on near-black in dark mode — and two settings pages had no
 * heading whatsoever. Copy-paste is how a page header spreads; this makes the
 * thing being copied correct.
 *
 * Scope: **top-level pages only.** A settings page that sits inside its own
 * settings shell (helpdesk's `/settings/*`, which has a second sidebar) is a
 * level deeper and its heading is a section heading, not a page title — use
 * `FormPageLayout` there, which renders `h2 text-lg` and also gates its content
 * region on load. Reaching for `PageHeader` inside such a shell produces two
 * competing `h1`s on one screen.
 *
 * The header is deliberately not load-gated: per the "Minimal dynamic UI"
 * convention the title and actions paint immediately and only the data region
 * below shows a loading state. If the title itself is fetched (a company name,
 * a contact), pass a small `animate-pulse` skeleton as `title` while it loads
 * rather than withholding the whole header.
 *
 * Known limitation: this is a single flex row with no breakpoint, so title and
 * actions never stack — on a narrow viewport the title compresses (`min-w-0`)
 * and the actions hold their width (`shrink-0`). A page whose header genuinely
 * needs to stack (`flex-col sm:flex-row`) stays hand-rolled for now. Adding
 * stacking here is a deliberate change, not a drive-by: every consumer's header
 * re-renders differently on mobile the moment it lands.
 *
 * Actions align to the **top** of the title block, not its middle. With a
 * `description` present that puts them level with the title rather than
 * floating between the two lines — intentional, and the reason converting a
 * hand-rolled `items-center` header moves its buttons up.
 *
 * @example
 * <PageHeader
 *   title={t('organizations.title')}
 *   description={t('organizations.subtitle')}
 *   actions={<Button onClick={onAdd}>Add organization</Button>}
 * />
 */
export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        {description && (
          <div className="mt-1 text-sm text-muted-foreground">{description}</div>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}
